import { useState, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import type { Employee } from '../../../types/employee';
import type { EmployeeSpreadsheetCellField, EditingCell } from '../types';

interface UseEmployeeInlineEditParams {
  employees: Employee[];
  updateEmployee: (employeeId: string, updates: Partial<Employee>, options?: { updateType?: 'fullUpdate' | 'newOnly'; reason?: string }) => Promise<void>;
  onSuccess?: (msg: string) => void;
  onError?: (msg: string) => void;
}

export function useEmployeeInlineEdit({
  employees,
  updateEmployee,
  onSuccess,
  onError,
}: UseEmployeeInlineEditParams) {
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);

  const handleSave = useCallback(
    async (
      employeeId: string,
      field: EmployeeSpreadsheetCellField,
      value: string | number | boolean
    ) => {
      const employee = employees.find((e) => e.id === employeeId);
      if (!employee) return;

      const updates: Partial<Employee> = {};
      if (field === 'name') {
        updates.name = String(value);
      } else if (field === 'hire_date') {
        const str = String(value);
        const d = new Date(str);
        if (!isNaN(d.getTime())) {
          updates.hire_date = Timestamp.fromDate(d);
        }
      } else if (field === 'isHourly') {
        updates.isHourly = value === true;
      } else if (field === 'hourlyRate') {
        const n = typeof value === 'number' ? value : parseInt(String(value).replace(/[,]/g, ''), 10) || 0;
        updates.hourlyRate = n;
      } else if (field === 'monthlySalary') {
        const n = typeof value === 'number' ? value : parseInt(String(value).replace(/[,]/g, ''), 10) || 0;
        updates.monthlySalary = n;
      } else if (field === 'payday') {
        updates.payday = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
      } else if (field === 'dependents') {
        updates.dependents = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
      }

      if (Object.keys(updates).length === 0) return;

      try {
        await updateEmployee(employeeId, updates);
        onSuccess?.('저장되었습니다.');
        setEditingCell(null);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : '저장 실패';
        onError?.(message);
      }
    },
    [employees, updateEmployee, onSuccess, onError]
  );

  return {
    editingCell,
    setEditingCell,
    handleSave,
    cancelEdit: useCallback(() => setEditingCell(null), []),
  };
}
